import { Lock, Mail, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { LockedSummary } from "../../shared/types";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Section } from "../components/ui/Section";
import { createCheckoutSession, getLockedSummary } from "../lib/testApi";

const fallbackMetrics = [
  "IQ Score",
  "Percentile",
  "Υποκλίμακες επίδοσης",
  "Ταχύτητα απόκρισης",
  "Δυσκολία που έφτασες",
  "Shareable badge",
  "PDF download",
  "Email report link"
];

function isLocalDevHost() {
  if (typeof window === "undefined") return false;
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

export function LockedResultPage() {
  const { attemptId } = useParams();
  const [summary, setSummary] = useState<LockedSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    if (!attemptId) return;
    getLockedSummary(attemptId)
      .then(setSummary)
      .catch((err) => setError(err instanceof Error ? err.message : "Δεν βρέθηκε η αναφορά."));
  }, [attemptId]);

  async function handleCheckout() {
    if (!attemptId) return;
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      const result = await createCheckoutSession(attemptId, checkoutEmail);
      window.location.href = result.checkoutUrl;
    } catch {
      setCheckoutError("Δεν μπορέσαμε να ξεκινήσουμε το ασφαλές checkout. Δοκίμασε ξανά σε λίγο.");
      setCheckoutLoading(false);
    }
  }

  if (error) {
    return (
      <Section className="py-12 sm:py-16">
        <Card className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold text-white">Δεν βρέθηκε η αναφορά</h1>
          <p className="mt-3 text-slate-300">Το test μπορεί να μην έχει ολοκληρωθεί ή το προσωρινό attempt να έχει λήξει.</p>
          <Button href="/test" className="mt-7">Επιστροφή στο test</Button>
        </Card>
      </Section>
    );
  }

  if (!summary) {
    return (
      <Section className="py-12 sm:py-16">
        <Card className="mx-auto max-w-2xl text-center">
          <p className="text-slate-300">Φορτώνουμε την προεπισκόπηση του report...</p>
        </Card>
      </Section>
    );
  }

  const metricLabels = [...new Set([...summary.lockedMetrics.map((metric) => metric.label), ...fallbackMetrics])];
  const showDevReportPreview = Boolean(attemptId && isLocalDevHost());

  return (
    <Section className="py-8 sm:py-14">
      <div className="mx-auto max-w-5xl">
        <div className="grid gap-6 lg:grid-cols-[1fr_0.85fr] lg:items-start">
          <div className="order-2 lg:order-1">
            <span className="inline-flex rounded-full border border-cyan-200/20 bg-cyan-200/10 px-3 py-1 text-xs font-semibold text-cyan-100">
              {summary.totalQuestions} scored ερωτήσεις ολοκληρώθηκαν
            </span>
            <h1 className="mt-5 text-4xl font-bold leading-tight text-white">{summary.teaserHeadline}</h1>
            <p className="mt-4 max-w-2xl leading-7 text-slate-300">
              Ολοκλήρωσες το Brain Test. Ξεκλείδωσε το πλήρες premium report για να δεις IQ Score, percentile-style ένδειξη,
              υποκλίμακες, χρόνο απόκρισης και προσωπική ανάλυση επίδοσης.
            </p>
            <Card className="mt-6">
              <p className="text-sm font-semibold text-cyan-100">Πρώτη εικόνα</p>
              <p className="mt-2 inline-flex rounded-full border border-white/10 bg-white/[0.055] px-3 py-1 text-xs font-semibold text-slate-200">
                {summary.performanceBand}
              </p>
              <p className="mt-3 leading-7 text-slate-300">{summary.teaserText}</p>
            </Card>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {metricLabels.map((label) => (
                <div key={label} className="flex min-h-20 items-center gap-3 rounded-lg border border-white/10 bg-white/[0.045] p-4">
                  <Lock className="shrink-0 text-cyan-200" size={20} />
                  <div>
                    <p className="font-semibold text-white">{label}</p>
                    <p className="text-xs text-slate-400">Κλειδωμένο στο premium report</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Card className="order-1 lg:order-2 lg:sticky lg:top-28">
            <ShieldCheck className="text-cyan-200" size={30} />
            <h2 className="mt-4 text-2xl font-bold text-white">Premium Report</h2>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              Αναλυτική παρουσίαση της επίδοσής σου, έτοιμη για online προβολή και αποθήκευση μέσω browser print.
            </p>
            <label className="mt-6 block text-sm font-semibold text-slate-200" htmlFor="premium-report-email">
              Email για πρόσβαση στο report
            </label>
            <input
              id="premium-report-email"
              type="email"
              value={checkoutEmail}
              onChange={(event) => setCheckoutEmail(event.target.value)}
              placeholder="you@example.com"
              className="mt-2 w-full rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-cyan-200/60 focus:ring-2 focus:ring-cyan-200/20"
            />
            <Button type="button" onClick={handleCheckout} disabled={checkoutLoading} className="mt-4 w-full">
              {checkoutLoading ? "Άνοιγμα checkout..." : `Ξεκλείδωμα Premium Report — ${summary.premiumPrice}`}
            </Button>
            {checkoutError ? <p className="mt-3 text-sm leading-5 text-rose-200">{checkoutError}</p> : null}
            <p className="mt-3 text-center text-sm text-slate-300">Άμεση πρόσβαση μετά την επιβεβαίωση πληρωμής</p>
            {showDevReportPreview ? (
              <Button href={`/report/demo/${attemptId}`} variant="secondary" className="mt-3 w-full">
                Dev preview report
              </Button>
            ) : null}
            <p className="mt-4 rounded-lg border border-white/10 bg-white/[0.045] p-3 text-xs leading-5 text-slate-400">
              Η πρόσβαση ενεργοποιείται μόλις επιβεβαιωθεί η πληρωμή σου.
            </p>
            <div className="mt-5 flex items-center gap-3 text-sm text-slate-300">
              <Mail className="shrink-0 text-cyan-200" size={22} />
              Αν συμπληρώσεις email, το Premium Report θα σταλεί και εκεί μετά την επιβεβαίωση πληρωμής.
            </div>
          </Card>
        </div>
        <p className="mt-7 rounded-lg border border-cyan-200/15 bg-cyan-200/[0.055] p-4 text-sm leading-6 text-slate-300">
          Η αναφορά επίδοσης είναι ενδεικτικό προφίλ απαντήσεων για προσωπική χρήση και σύγκριση προόδου.
        </p>
      </div>
    </Section>
  );
}
